/**
 * PoolCustodyMapModal — « où est Thomas Roussel ? » : là, sur la carte.
 *
 * Le modal ouvert depuis l'encart « En ce moment » (`PoolPresenceCard`). Il ne
 * recharge RIEN : la garde lui est passée telle que la fiche l'a déjà lue. Un
 * second appel pour dessiner ce qu'on a déjà, c'est une latence gratuite devant
 * un chef d'U.T. qui attend une réponse.
 *
 * Trois points, et seulement ceux qui existent :
 *   départ  = l'U.T. d'attache      (`route.origin`)
 *   véhicule = la dernière POSITION GPS remontée par le boîtier
 *              (`route.vehicle_position`, vocabulaire `vehicle_telemetry`)
 *   arrivée = la destination géolocalisée (`route.destination`)
 *
 * ⚠️ LA POSITION GPS N'EST PAS UNE PREUVE DE PRÉSENCE. Elle vient du boîtier
 * du véhicule, pas de l'agent : un véhicule sans boîtier n'a aucun point, et le
 * modal le DIT au lieu de laisser croire à une panne. C'est le sens de la note
 * en pied de panneau — elle n'est pas décorative, elle évite qu'on fonde une
 * décision de gestion sur un silence technique.
 *
 * ⚠️ Une destination peut être renseignée SANS être géolocalisée
 * (`destination_label` sans `route.destination`) : on l'affiche quand même, en
 * annonçant qu'elle n'est pas sur la carte.
 *
 * Lecture seule, comme l'encart : aucun geste de gestion ici.
 */
import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import { useTranslation } from 'react-i18next';
import { X, MapPin, Building2, Flag, Car, User, Clock, Gauge } from 'lucide-react';
import { useFormat } from '../../i18n/format.js';
import { formatDuration } from './PoolCustodyPanel.jsx';
// `L` vient de MapWrapper : c'est lui qui porte la correction des icônes
// Leaflet par défaut (`L.Icon.Default.mergeOptions`) pour toute l'application.
// La réécrire ici en ferait une seconde source de vérité.
import { L } from '../map/MapWrapper.jsx';

/** Mêmes tuiles que `GPSLiveMap` — une seule source de fond de carte. */
const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

/** Centre de repli : jamais utilisé quand un point existe, `fitBounds` reprend la main. */
const FALLBACK_CENTER = [46.603354, 1.888334];

const PIN_GLYPH = { origin: '\u{1F3E2}', vehicle: '\u{1F690}', destination: '\u{1F3C1}' };
const PIN_COLOR = { origin: '#6b7280', vehicle: '#2563eb', destination: '#16a34a' };

/** Une pastille colorée, sans texte : le libellé vit dans le Popup, donc traduisible. */
function pinIcon(kind) {
  if (!L || typeof L.divIcon !== 'function') return undefined;
  return L.divIcon({
    className: '',
    html:
      `<div style="width:32px;height:32px;border-radius:9999px;background:${PIN_COLOR[kind]};`
      + 'border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);display:flex;'
      + `align-items:center;justify-content:center;font-size:16px;">${PIN_GLYPH[kind]}</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
}

/**
 * « CD-113-31 · Renault Kangoo ».
 *
 * Recopié de `PoolPresenceCard` à dessein : l'importer d'ici créerait un cycle
 * d'imports entre l'encart et son modal, pour quatre lignes.
 */
function vehicleIdentity(source) {
  if (!source) return null;
  const parts = [source.license_plate, source.vehicle_label].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

/** Un point utilisable : latitude et longitude numériques et plausibles. */
function usablePoint(raw) {
  if (!raw) return null;
  const lat = Number(raw.lat);
  const lng = Number(raw.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { ...raw, lat, lng };
}

/**
 * Cadre la carte sur les points PRÉSENTS.
 *
 * Un zoom fixe cadrerait juste par hasard : entre une garde de quartier et une
 * tournée départementale, il y a deux ordres de grandeur.
 */
export function FitToRoute({ points }) {
  const map = useMap();

  useEffect(() => {
    if (!map || typeof map.fitBounds !== 'function') return;
    if (!points || points.length === 0) return;
    try {
      map.fitBounds(points.map((p) => [p.lat, p.lng]), { padding: [48, 48], maxZoom: 15 });
    } catch (err) {
      // Carte pas encore prête : le centre de repli fait l'affaire.
      console.warn('fitBounds failed:', err);
    }
  }, [map, points]);

  return null;
}

/** Une ligne « étiquette / valeur » du panneau, avec son icône. */
function InfoRow({ icon: Icon, label, children, tone = 'text-gray-400' }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${tone}`} />
      <div className="min-w-0 flex-1">
        <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
        <div className="text-sm text-gray-900">{children}</div>
      </div>
    </div>
  );
}

/**
 * @param {Object} props
 * @param {Object} props.custody - la garde en cours, telle que rendue par `poolPresenceAPI`
 * @param {'driver'|'vehicle'} props.kind - depuis quelle fiche on ouvre
 * @param {Function} props.onClose - fermeture (✕, fond, Échap)
 */
/**
 * `upcoming` : la réservation n'est pas encore prise (retour JGS 14/09). Les
 * lignes deviennent « Départ prévu / Retour prévu », sans retard possible, et
 * la position se lit comme « garé », pas comme « en route ».
 */
export function PoolCustodyMapModal({ custody, kind, onClose, upcoming = false }) {
  const { t } = useTranslation();
  const { fmt } = useFormat();

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const route = custody?.route || null;
  const origin = usablePoint(route?.origin);
  const destination = usablePoint(route?.destination);
  const position = usablePoint(route?.vehicle_position);

  const points = useMemo(
    () => [origin, position, destination].filter(Boolean),
    [origin, position, destination]
  );

  if (!custody) return null;

  const dash = t('poolPresence.map.unknown');

  // Même jour que maintenant : l'heure suffit ; sinon la date, sans quoi
  // « 17:45 » d'hier se lit comme « tout à l'heure ».
  const stamp = (value) => {
    if (!value) return null;
    const when = new Date(value);
    if (Number.isNaN(when.getTime())) return null;
    const ref = new Date();
    const sameDay = when.getFullYear() === ref.getFullYear()
      && when.getMonth() === ref.getMonth()
      && when.getDate() === ref.getDate();
    return sameDay ? fmt.time(when) : fmt.dateTime(when);
  };

  const positionTime = stamp(position?.timestamp);
  const positionPopup = position && position.speed_kmh != null
    ? t('poolPresence.map.positionAt', { time: positionTime || dash, speed: position.speed_kmh })
    : t('poolPresence.map.positionAtNoSpeed', { time: positionTime || dash });

  const vehicleName = vehicleIdentity(custody) || t('poolPresence.driver.unknownVehicle');
  const driverName = custody.driver_label || t('poolPresence.vehicle.unknownDriver');
  const headline = kind === 'driver' ? vehicleName : driverName;

  const polyline = points.length >= 2 ? points.map((p) => [p.lat, p.lng]) : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="flex h-[80vh] w-[960px] max-w-full flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex shrink-0 items-center justify-between bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-4">
          <div className="flex min-w-0 items-center gap-2">
            <MapPin className="h-5 w-5 shrink-0 text-white" />
            <h2 className="truncate text-lg font-semibold text-white">
              {t('poolPresence.map.title', { subject: headline })}
            </h2>
          </div>
          <button
            type="button"
            onClick={() => onClose?.()}
            className="text-white/80 hover:text-white"
            aria-label={t('poolPresence.map.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          <div className="min-h-0 flex-1 md:basis-2/3">
            {points.length === 0 ? (
              <div className="flex h-full items-center justify-center bg-gray-50 p-8">
                <div className="text-center">
                  <MapPin className="mx-auto mb-3 h-12 w-12 text-gray-300" />
                  <p className="text-sm text-gray-600">{t('poolPresence.map.noPosition')}</p>
                </div>
              </div>
            ) : (
              <MapContainer
                center={points.length ? [points[0].lat, points[0].lng] : FALLBACK_CENTER}
                zoom={12}
                style={{ height: '100%', width: '100%' }}
                className="z-0"
              >
                <TileLayer attribution={TILE_ATTRIBUTION} url={TILE_URL} />
                <FitToRoute points={points} />

                {polyline && (
                  <Polyline
                    positions={polyline}
                    pathOptions={{ color: '#2563eb', weight: 3, dashArray: '6 8', opacity: 0.8 }}
                  />
                )}

                {origin && (
                  <Marker position={[origin.lat, origin.lng]} icon={pinIcon('origin')}>
                    <Popup>{origin.label || t('poolPresence.map.origin')}</Popup>
                  </Marker>
                )}

                {position && (
                  <Marker position={[position.lat, position.lng]} icon={pinIcon('vehicle')}>
                    <Popup>{positionPopup}</Popup>
                  </Marker>
                )}

                {destination && (
                  <Marker position={[destination.lat, destination.lng]} icon={pinIcon('destination')}>
                    <Popup>{destination.label || t('poolPresence.map.destination')}</Popup>
                  </Marker>
                )}
              </MapContainer>
            )}
          </div>

          <div className="min-h-0 space-y-4 overflow-y-auto border-t border-gray-200 p-5 md:w-1/3 md:shrink-0 md:border-l md:border-t-0">
            <InfoRow icon={User} label={t('poolPresence.map.agent')}>
              <span className="font-semibold">{driverName}</span>
            </InfoRow>

            <InfoRow icon={Car} label={t('poolPresence.map.vehicle')}>
              <span className="font-semibold">{vehicleName}</span>
            </InfoRow>

            <InfoRow icon={Clock} label={t(upcoming ? 'poolPresence.map.plannedStart' : 'poolPresence.map.takenAt')}>
              {stamp(upcoming ? custody.planned_start : custody.taken_at) || dash}
            </InfoRow>

            <InfoRow
              icon={Clock}
              label={t(upcoming ? 'poolPresence.map.plannedEnd' : 'poolPresence.map.dueAt')}
              tone={!upcoming && custody.late ? 'text-red-500' : 'text-gray-400'}
            >
              <span>{stamp(upcoming ? custody.planned_end : custody.due_at) || dash}</span>
              {upcoming ? null : custody.late ? (
                <span className="ml-2 font-semibold text-red-600">
                  {t('poolPresence.late', { duration: formatDuration(custody.overdue_min) })}
                </span>
              ) : custody.remaining_min != null ? (
                <span className="ml-2 text-gray-500">
                  {t('poolPresence.remaining', { duration: formatDuration(custody.remaining_min) })}
                </span>
              ) : null}
            </InfoRow>

            <InfoRow icon={Flag} label={t('poolPresence.map.destination')}>
              <span>{custody.destination_label || dash}</span>
              {custody.destination_label && !destination && (
                <span className="ml-2 text-xs text-gray-500">
                  {t('poolPresence.map.destinationNotGeolocated')}
                </span>
              )}
            </InfoRow>

            <InfoRow icon={Building2} label={t('poolPresence.map.origin')}>
              {origin?.label || route?.origin?.label || dash}
            </InfoRow>

            <InfoRow icon={Gauge} label={t(upcoming ? 'poolPresence.map.parkedPosition' : 'poolPresence.map.lastPosition')}>
              {position ? (
                <div className="space-y-0.5">
                  <p>{positionPopup}</p>
                  {position.age_min != null && (
                    <p className="text-xs text-gray-500">
                      {t('poolPresence.map.ago', { duration: formatDuration(position.age_min) })}
                    </p>
                  )}
                  {position.odometer_km != null && (
                    <p className="text-xs text-gray-500">
                      {t('poolPresence.map.odometer', { km: position.odometer_km })}
                    </p>
                  )}
                </div>
              ) : (
                <span className="text-gray-500">{t('poolPresence.map.noDevice')}</span>
              )}
            </InfoRow>

            <p className="border-t border-gray-100 pt-3 text-xs leading-relaxed text-gray-500">
              {t('poolPresence.map.disclaimer')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PoolCustodyMapModal;
